// PROA_TRIAL_30D_UNIFIED_V1
// frontend/src/main.tsx
// v8.0.0 - IMPLEMENTACIÓN DE ARQUITECTURA HÍBRIDA (PUBLIC/PRIVATE SPLIT)

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Route,
  createRoutesFromElements,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './config/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';

// PRC-015: página de muestra del recorrido, solo en desarrollo (no entra en el build).
const JourneyShowcasePage = import.meta.env.DEV
  ? React.lazy(() => import('./modules/pricingJourney/pages/JourneyShowcasePage'))
  : null;

// --- IMPORTACIÓN DE PÁGINAS ---
// 1. Auth & Standalone
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import SessionExpiredPage from './pages/SessionExpiredPage';
import UpdatePasswordPage from './pages/UpdatePasswordPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage'; // Standalone para evitar interferencias
import NotFoundPage from './pages/NotFoundPage';
import { AppErrorPage, AppNotFoundPage } from './modules/pricingJourney/pages/AppStatusPages';

// 2. Públicas (Landing & Marketing)
import LandingPage from './pages/LandingPage'; // <-- NUEVA
import ProaLandingPage from './pages/ProaLandingPage';
import IndustrialLandingPage from './pages/IndustrialLandingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import MethodologyPage from './pages/MethodologyPage';
import PublicFileUploadPage from './pages/PublicFileUploadPage';

// 3. Privadas (App Core)
import DataUploadPage from './pages/DataUploadPage';
import DataQualityPage from './pages/DataQualityPage';
import ConnectionsPage from './pages/ConnectionsPage';
import RetrainingPage from './pages/RetrainingPage';
import OptimizationHubPage from './pages/OptimizationHubPage';
import OptimizationPage from './pages/OptimizationPage';
import OptimizationHistoryPage from './pages/OptimizationHistoryPage';
import OptimizationEvaluationPage from './pages/OptimizationEvaluationPage';
import BatchOptimizationPage from './pages/BatchOptimizationPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PlansPage from './modules/pricingJourney/pages/PlansPage';
import PricingTeamPage from './pages/PricingTeamPage';
import ProfilePage from './pages/ProfilePage';
import CostManagementPage from './pages/CostManagementPage';
import OperationsPage from './pages/OperationsPage';
import MarginCenterOverviewPage from './modules/marginCenter/pages/MarginCenterOverviewPage';
import MarginCenterExplorerPage from './modules/marginCenter/pages/MarginCenterExplorerPage';
import MarginCenterCustomerPage from './modules/marginCenter/pages/MarginCenterCustomerPage';
import MarginCenterProductPage from './modules/marginCenter/pages/MarginCenterProductPage';
import MarginCenterFollowUpInventoryPage from './modules/marginCenter/pages/MarginCenterFollowUpInventoryPage';
import MarginCenterCadenceAttentionPage from './modules/marginCenter/pages/MarginCenterCadenceAttentionPage';
import MarginCenterConsumptionObservationsPage from './modules/marginCenter/pages/MarginCenterConsumptionObservationsPage';
import MarginCenterEconomicPriceObservationsPage from './modules/marginCenter/pages/MarginCenterEconomicPriceObservationsPage';
import MarginCenterFollowUpPlanningPage from './modules/marginCenter/pages/MarginCenterFollowUpPlanningPage';
import ProcurementUploadPage from './pages/ProcurementUploadPage';
import ProcurementStrategyPage from './pages/ProcurementStrategyPage';
import ProcurementAnalysisPage from './pages/ProcurementAnalysisPage';

import AuditorPage from './pages/AuditorPage';
import IndustrialRiskPage from './modules/industrialRisk/pages/IndustrialRiskPage';
import IndustrialCostPage from './modules/industrialCost/pages/IndustrialCostPage';
import IndustrialCostLoginPage from './modules/industrialCost/pages/IndustrialCostLoginPage';
import IndustrialCostFactoryLoginPage from './modules/industrialCost/pages/IndustrialCostFactoryLoginPage';
import IndustrialCostFactoryOperatorPage from './modules/industrialCost/pages/IndustrialCostFactoryOperatorPage';
import IndustrialCostFactoryEntryPage from './modules/industrialCost/pages/IndustrialCostFactoryEntryPage';
import IndustrialWorkflowsOperatorPage from './modules/industrialWorkflows/pages/IndustrialWorkflowsOperatorPage';
import IndustrialCostProtectedRoute from './modules/industrialCost/components/IndustrialCostProtectedRoute';
import IndustrialCostFactoryProtectedRoute from './modules/industrialCost/components/IndustrialCostFactoryProtectedRoute';
import IndustrialCostFactoryAccessGuard from './modules/industrialCost/components/IndustrialCostFactoryAccessGuard';
import PokerStudyProtectedRoute from './modules/pokerStudy/components/PokerStudyProtectedRoute';
import PokerStudyLandingPage from './modules/pokerStudy/pages/PokerStudyLandingPage';
import PokerStudyLoginPage from './modules/pokerStudy/pages/PokerStudyLoginPage';
import PokerStudyPage from './modules/pokerStudy/pages/PokerStudyPage';
import PokerStudySpotPage from './modules/pokerStudy/pages/PokerStudySpotPage';
import PokerStudyBoardPage from './modules/pokerStudy/pages/PokerStudyBoardPage';
import PlatformAdminRoute from './modules/platformAdmin/components/PlatformAdminRoute';
import PlatformAdminBootstrapRoute from './modules/platformAdmin/components/PlatformAdminBootstrapRoute';
import PlatformAdminPage from './modules/platformAdmin/pages/PlatformAdminPage';
import PlatformAdminBootstrapPage from './modules/platformAdmin/pages/PlatformAdminBootstrapPage';
import StacAuthenticatedRoute from './modules/stac/components/StacAuthenticatedRoute';
import StacPage from './modules/stac/pages/StacPage';

import ProjectVitalProtectedRoute from './modules/projectVital/components/ProjectVitalProtectedRoute';
import ProjectVitalLoginPage from './modules/projectVital/pages/ProjectVitalLoginPage';
import ProjectVitalPage from './modules/projectVital/pages/ProjectVitalPage';

// --- IMPORTACIÓN DE LAYOUTS Y CORE ---
import App from './App'; 
import MainLayout from './layouts/MainLayout';
import PublicLayout from './layouts/PublicLayout'; // <-- NUEVO
import { AuthProvider, isPricingFreePath, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PricingAdminRoute from './components/PricingAdminRoute';
import PricingAccessRoute from './components/PricingAccessRoute';
import WelcomePage from './modules/pricingJourney/pages/WelcomePage';
import FirstStepsPage from './modules/pricingJourney/pages/FirstStepsPage';
import AccountConfirmedPage from './modules/pricingJourney/pages/AccountConfirmedPage';
import RecoverPasswordPage from './modules/pricingJourney/pages/RecoverPasswordPage';
import AppVersionUpdateNotifier from './components/AppVersionUpdateNotifier';
import theme from './styles/theme';
import * as Sentry from "@sentry/react";

// --- INICIALIZACIÓN DE SENTRY ---
// Política conservadora: capturar errores técnicos sin enviar datos sensibles de negocio.
const SENTRY_ENVIRONMENT = import.meta.env.VITE_APP_ENVIRONMENT || 'development';
const SENTRY_RELEASE =
  import.meta.env.VITE_APP_RELEASE ||
  import.meta.env.VITE_COMMIT_SHA ||
  undefined;

const redactSensitiveKeys = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const sensitiveMarkers = [
    'authorization',
    'cookie',
    'set-cookie',
    'token',
    'apikey',
    'api-key',
    'password',
    'secret',
    'payload',
    'excel',
    'file',
  ];

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
    (acc, [key, entryValue]) => {
      const normalizedKey = key.toLowerCase();

      if (sensitiveMarkers.some((marker) => normalizedKey.includes(marker))) {
        return acc;
      }

      acc[key] = entryValue;
      return acc;
    },
    {}
  );
};

const sanitizeSentryEvent = <TEvent extends Sentry.Event>(event: TEvent): TEvent | null => {
  const request = event.request as Record<string, unknown> | undefined;

  if (request) {
    delete request.cookies;
    delete request.data;

    if (request.headers) {
      request.headers = redactSensitiveKeys(request.headers);
    }
  }

  const user = event.user as Record<string, unknown> | undefined;

  if (user) {
    delete user.email;
    delete user.ip_address;
    delete user.username;
  }

  if (event.extra) {
    event.extra = redactSensitiveKeys(event.extra) as Record<string, unknown>;
  }

  return event;
};

const sanitizeSentryBreadcrumb = (
  breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb | null => {
  if (breadcrumb.data) {
    breadcrumb.data = redactSensitiveKeys(breadcrumb.data) as Record<string, unknown>;
  }

  return breadcrumb;
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_FRONTEND_DSN,
  enabled: Boolean(import.meta.env.VITE_SENTRY_FRONTEND_DSN),
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  sendDefaultPii: false,
  tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.05 : 0.1,
  replaysSessionSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.01 : 0.05,
  replaysOnErrorSampleRate: 1.0,
  environment: SENTRY_ENVIRONMENT,
  release: SENTRY_RELEASE,
  beforeSend: sanitizeSentryEvent,
  beforeBreadcrumb: sanitizeSentryBreadcrumb,
});

// --- REACT QUERY ---
// IND-058: un solo cliente (config/queryClient), el mismo que limpia el cierre de sesión.

// --- COMPONENTE CONTROLADOR DE TRÁFICO (INDEX RESOLVER) ---
// Decide si mostrar la Landing o redirigir al Dashboard
const IndexResolver = () => {
    const { isAuthenticated, isLoading, industrialCostsRole } = useAuth();

    if (isLoading) {
        return (
            <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (isAuthenticated) {
        if (industrialCostsRole === 'factory_operator') {
            return <Navigate to="/factory/entry" replace />;
        }

        if (industrialCostsRole === 'factory_manager') {
            return <Navigate to="/industrial-cost" replace />;
        }

        return <Navigate to="/margin-center/overview" replace />;
    }

    // Si no, le vendemos el producto (Landing)
    return <LandingPage />;
};


// IND-056: fuera de Control industrial, el contexto de Pricing se pide en cuanto
// se entra (si se abrió la app en Control industrial, aún no se había pedido).
const PricingContextLoader = () => {
  const location = useLocation();
  const { ensurePricingContext } = useAuth();
  useEffect(() => {
    if (!isPricingFreePath(location.pathname)) ensurePricingContext();
  }, [location.pathname, ensurePricingContext]);
  return null;
};

const AppRoot = () => (
  <>
    <AppVersionUpdateNotifier />
    <PricingContextLoader />
    <App />
  </>
);

// --- DEFINICIÓN DE RUTAS ---
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<AppRoot />} errorElement={<AppErrorPage />}>

      {/* Proyecto Vital es un módulo independiente de Pricing e Industrial. */}
      <Route path="/project-vital/login" element={<ProjectVitalLoginPage />} />

      <Route element={<ProjectVitalProtectedRoute />}>
        <Route path="/project-vital" element={<ProjectVitalPage />} />
      </Route>

      {/* STAC es una capacidad independiente de Industrial. */}
      <Route element={<StacAuthenticatedRoute />}>
        <Route path="/stac" element={<StacPage />} />
      </Route>

      {/* Platform Admin también es independiente de Industrial. */}
      <Route element={<PlatformAdminBootstrapRoute />}>
        <Route
          path="/platform-admin/bootstrap"
          element={<PlatformAdminBootstrapPage />}
        />
      </Route>

      <Route element={<PlatformAdminRoute />}>
        <Route
          path="/platform-admin"
          element={<PlatformAdminPage />}
        />
      </Route>

      <Route element={<IndustrialCostFactoryAccessGuard />}>

        {/* 1. RUTAS STANDALONE (Sin Layouts complejos) */}
        <Route path="session-expired" element={<SessionExpiredPage />} /> 
        <Route path="update-password" element={<UpdatePasswordPage />} />
        {/* PRC-016 / PRC-017: acceso y alta a pantalla completa (bocetos A1–A5). */}
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignUpPage />} />
        <Route path="cuenta-confirmada" element={<AccountConfirmedPage />} />
        <Route path="recuperar-contrasena" element={<RecoverPasswordPage />} />
        <Route path="payment-callback" element={<PaymentCallbackPage />} />
        <Route path="factory/login" element={<IndustrialCostFactoryLoginPage />} />
        <Route path="industrial-cost/login" element={<IndustrialCostLoginPage />} />
        <Route path="poker" element={<PokerStudyLandingPage />} />
        <Route path="poker/login" element={<PokerStudyLoginPage />} />
        {JourneyShowcasePage && (
          <Route path="dev/recorrido" element={<React.Suspense fallback={null}><JourneyShowcasePage /></React.Suspense>} />
        )}

        {/* 2. RUTAS PÚBLICAS (Con PublicLayout - Navbar Transparente) */}
        <Route element={<PublicLayout />}>
            {/* La raíz '/' ahora usa el Resolver */}
            <Route index element={<IndexResolver />} />

            <Route path="proa" element={<ProaLandingPage />} />
            <Route path="industrial" element={<IndustrialLandingPage />} />
            
            <Route path="methodology" element={<MethodologyPage />} /> 
            
            {/* /pricing lleva a la sección de precios de la portada */}
            <Route path="pricing" element={<Navigate to="/" replace state={{ targetSection: 'pricing-section' }} />} />
            <Route path="terms" element={<TermsPage />} />
            <Route path="privacy" element={<PrivacyPolicyPage />} />
            <Route path="/subir-archivos" element={<PublicFileUploadPage />} />
            
        </Route>

        {/* 3A. RUTAS PROTEGIDAS DE FÁBRICA / INDUSTRIAL COSTS (Sin layout general) */}
        <Route element={<IndustrialCostFactoryProtectedRoute />}>
          <Route path="/factory/entry" element={<IndustrialCostFactoryEntryPage />} />
          <Route path="/factory/operator" element={<IndustrialCostFactoryOperatorPage />} />
          <Route path="/industrial-workflows/operator" element={<IndustrialWorkflowsOperatorPage />} />
        </Route>

        <Route element={<IndustrialCostProtectedRoute />}>
          <Route path="/industrial-cost" element={<IndustrialCostPage />} />
        </Route>

        {/* 3B. RUTAS PROTEGIDAS POKER STUDY (Sin layout general PROA) */}
        <Route element={<PokerStudyProtectedRoute />}>
          <Route path="/poker-study" element={<PokerStudyPage />} />
          <Route path="/poker-study/spots/:spotId" element={<PokerStudySpotPage />} />
          <Route path="/poker-study/boards/:boardId" element={<PokerStudyBoardPage />} />
        </Route>

        {/* 3C. RUTAS PROTEGIDAS GENERALES (Con MainLayout - Sidebar Operativo) */}
        <Route element={<ProtectedRoute />}>

          <Route path="/industrial-risk" element={<IndustrialRiskPage />} />

          <Route element={<PricingAccessRoute />}>
            <Route path="bienvenida" element={<WelcomePage />} />
            <Route element={<MainLayout />}>
            <Route path="primeros-pasos" element={<FirstStepsPage />} />
            
            {/* PRC-027: pantallas retiradas; sus direcciones abren Overview. */}
            <Route path="dashboard" element={<Navigate to="/margin-center/overview" replace />} />

            <Route
              path="margin-center/overview"
              element={<MarginCenterOverviewPage />}
            />

            <Route
              path="margin-center/explorer"
              element={<MarginCenterExplorerPage />}
            />

            <Route
              path="margin-center/customers/:customerId"
              element={<MarginCenterCustomerPage />}
            />

            <Route
              path="margin-center/products/:productId"
              element={<MarginCenterProductPage />}
            />

            {/* DUX-03D: memoria comercial común, sin navegación lateral propia. */}
            <Route
              path="margin-center/control-comercial"
              element={<MarginCenterFollowUpInventoryPage />}
            />

            <Route
              path="margin-center/control-comercial/avisos/cadencia"
              element={<MarginCenterCadenceAttentionPage />}
            />

            <Route
              path="margin-center/control-comercial/avisos/consumo"
              element={<MarginCenterConsumptionObservationsPage />}
            />

            <Route
              path="margin-center/control-comercial/avisos/condiciones-economicas"
              element={<MarginCenterEconomicPriceObservationsPage />}
            />

            <Route
              path="margin-center/control-comercial/planificacion"
              element={<MarginCenterFollowUpPlanningPage />}
            />
            
            <Route path="data-upload" element={<DataUploadPage />} />
            {/* PRC-027: «Enviar archivos al equipo» con sesión, dentro de la aplicación. */}
            <Route path="enviar-archivos" element={<PublicFileUploadPage inApp />} />
            <Route path="procurement" element={<ProcurementUploadPage />} />
            <Route path="procurement/strategy" element={<ProcurementStrategyPage />} />
            <Route path="procurement/analysis/:familyId" element={<ProcurementAnalysisPage />} />
            <Route path="data-diagnosis" element={<Navigate to="/margin-center/overview" replace />} />
            <Route path="data-quality" element={<DataQualityPage />} />
            <Route path="connections" element={<ConnectionsPage />} />
            <Route path="retraining" element={<RetrainingPage />} />
            <Route path="operations" element={<OperationsPage />} />
            <Route path="optimization-center" element={<OptimizationHubPage />} />
            <Route path="optimization" element={<OptimizationPage />} />
            <Route path="optimization/history" element={<OptimizationHistoryPage />} />
            <Route path="optimization/evaluations/:optimizationId" element={<OptimizationEvaluationPage />} />
            <Route path="batch-optimization" element={<BatchOptimizationPage />} />
            <Route element={<PricingAdminRoute />}>
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="planes" element={<PlansPage />} />
              <Route path="team" element={<PricingTeamPage />} />
            </Route>
            <Route path="profile" element={<ProfilePage />} />
            <Route path="historical-costs" element={<CostManagementPage />} />
            {/* PRC-039: Planificación retirada de Pricing hasta Autonomy; el código se conserva. */}
            <Route path="predictions" element={<Navigate to="/margin-center/overview" replace />} />
            <Route path="diagnosis/profit-leakage" element={<Navigate to="/margin-center/overview" replace />} />
            
            <Route path="auditor" element={<AuditorPage />} /> 
            {/* PRC-028: direcciones inexistentes de Pricing, dentro del layout y con el menú. */}
            <Route path="margin-center/*" element={<AppNotFoundPage />} />
            <Route path="optimization/*" element={<AppNotFoundPage />} />
            </Route>
          </Route>
        </Route>

        {/* 404 Catch-all */}
      <Route path="*" element={<NotFoundPage />} />

    </Route>
  </Route>
    )
  );

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error("No se encontró el elemento id 'root'");

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Sentry.ErrorBoundary
              fallback={
                <Box
                  sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  Se ha producido un error inesperado. Recarga la página o vuelve a intentarlo en unos minutos.
                </Box>
              }
            >
              <RouterProvider router={router} />
            </Sentry.ErrorBoundary>
          </SnackbarProvider>
        </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
);